import { Node, Edge } from 'reactflow';
import { getResourceConfig } from '../Config/resourceConfigProvider';

export interface ValidationIssue {
  id: string;
  nodeId: string;
  severity: 'error' | 'warning' | 'info';
  message: string;
  field?: string;
}

// Valid Azure regions
const VALID_AZURE_REGIONS = [
  'eastus', 'westus', 'centralus', 'northeurope', 'westeurope',
  'southeastasia', 'eastasia', 'australiaeast', 'canadacentral',
  'uksouth', 'francecentral', 'germanywestcentral', 'switzerlandnorth'
];

export const validateNode = (node: Node): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];

  // Skip validation for non-resource nodes
  if (node.type !== 'custom' || !node.data.type) {
    return issues;
  }

  const resourceType = node.data.type;
  const params = node.data.params || {};

  // Get resource configuration
  const config = getResourceConfig(resourceType);

  if (config) {
    // Validate using the new configuration system
    config.fields.forEach(field => {
      if (field.required) {
        const value = params[field.key];
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          issues.push({
            id: `${node.id}-${field.key}-required`,
            nodeId: node.id,
            severity: 'error',
            message: `Le champ "${field.label}" est requis`,
            field: field.key,
          });
        }
      }

      // Run custom validation if provided
      if (field.validation && params[field.key]) {
        const validationError = field.validation(params[field.key]);
        if (validationError) {
          issues.push({
            id: `${node.id}-${field.key}-validation`,
            nodeId: node.id,
            severity: 'error',
            message: validationError,
            field: field.key,
          });
        }
      }
    });
  }

  // Additional validation warnings (non-critical)
  if (params.location) {
    const location = params.location.toString().toLowerCase().replace(/\s/g, '');
    if (!VALID_AZURE_REGIONS.includes(location)) {
      issues.push({
        id: `${node.id}-location-warning`,
        nodeId: node.id,
        severity: 'info',
        message: `La région "${params.location}" pourrait ne pas être optimale. Vérifiez la disponibilité`,
        field: 'location',
      });
    }
  }

  return issues;
};

export const validateEdge = (edge: Edge, nodes: Node[]): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];

  const sourceNode = nodes.find(n => n.id === edge.source);
  const targetNode = nodes.find(n => n.id === edge.target);

  if (!sourceNode || !targetNode) {
    return issues;
  }

  // Skip validation for non-resource nodes
  if (sourceNode.type !== 'custom' || targetNode.type !== 'custom') {
    return issues;
  }

  // Validate common connection patterns
  const sourceType = sourceNode.data.type;
  const targetType = targetNode.data.type;

  // Example: Subnet should be in a VNet
  if (sourceType === 'azurerm_virtual_network' && targetType !== 'azurerm_subnet') {
    issues.push({
      id: `${edge.id}-vnet-connection`,
      nodeId: edge.source,
      severity: 'info',
      message: `Un Virtual Network est généralement connecté à des Subnets`,
    });
  }

  // Example: VM should have network interface
  if (targetType === 'azurerm_virtual_machine') {
    // Check if source is appropriate
    const validSources = ['azurerm_network_interface', 'azurerm_subnet', 'azurerm_lb'];
    if (!validSources.some(type => sourceType.includes(type))) {
      issues.push({
        id: `${edge.id}-vm-connection`,
        nodeId: edge.target,
        severity: 'warning',
        message: `Une VM devrait être connectée à une interface réseau, un subnet ou un load balancer`,
      });
    }
  }

  return issues;
};

const FABRIC_ITEM_TYPES = new Set([
  'fabric_lakehouse', 'fabric_warehouse', 'fabric_sql_database', 'fabric_onelake',
  'fabric_pipeline', 'fabric_dataflow', 'fabric_notebook',
  'fabric_semantic_model', 'fabric_report', 'fabric_ml_model', 'fabric_data_agent',
  'fabric_eventstream', 'fabric_eventhouse', 'fabric_kql_database',
  'fabric_deployment_pipeline',
]);

const FABRIC_SKUS = ['Trial', 'F2', 'F4', 'F8', 'F16', 'F32', 'F64', 'F128'];

function validateFabricCanvas(nodes: Node[]): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const nodeMap = new Map(nodes.map(n => [n.id, n]));

  const fabricCapacities  = nodes.filter(n => n.data?.type === 'fabric_capacity');
  const fabricWorkspaces  = nodes.filter(n => n.data?.type === 'fabric_workspace');
  const fabricFolders     = nodes.filter(n => n.data?.type === 'fabric_folder');
  const fabricItems       = nodes.filter(n => FABRIC_ITEM_TYPES.has(n.data?.type));

  // No Fabric nodes → skip
  if (fabricCapacities.length === 0 && fabricWorkspaces.length === 0 && fabricItems.length === 0) {
    return issues;
  }

  // 1. At least one fabric_capacity required
  if (fabricCapacities.length === 0) {
    issues.push({
      id: 'fabric-no-capacity',
      nodeId: fabricWorkspaces[0]?.id || fabricItems[0]?.id || '',
      severity: 'error',
      message: 'Architecture Fabric : aucune Fabric Capacity trouvée. Ajoutez un nœud fabric_capacity comme conteneur racine.',
    });
  }

  // 2. fabric_workspace must be inside a fabric_capacity (directly, or via a fabric_domain)
  fabricWorkspaces.forEach(ws => {
    const parent = ws.parentNode ? nodeMap.get(ws.parentNode) : undefined;
    if (!ws.parentNode || !parent) {
      issues.push({
        id: `${ws.id}-ws-no-parent`,
        nodeId: ws.id,
        severity: 'error',
        message: `Le Workspace "${ws.data?.label || ws.id}" n'est dans aucune Fabric Capacity. Glissez-le dans une capacity (ou dans un domaine qui lui-même est dans une capacity).`,
      });
    } else if (parent.data?.type === 'fabric_capacity') {
      // OK — directly in capacity
    } else if (parent.data?.type === 'fabric_domain') {
      // Valid if the domain itself is in a capacity
      const grandparent = parent.parentNode ? nodeMap.get(parent.parentNode) : undefined;
      if (!grandparent || grandparent.data?.type !== 'fabric_capacity') {
        issues.push({
          id: `${ws.id}-ws-domain-no-capacity`,
          nodeId: ws.id,
          severity: 'error',
          message: `Le Workspace "${ws.data?.label || ws.id}" est dans le Domaine "${parent.data?.label || parent.id}" qui n'est pas dans une Fabric Capacity.`,
        });
      }
      // else OK — workspace in domain in capacity
    } else {
      issues.push({
        id: `${ws.id}-ws-wrong-parent`,
        nodeId: ws.id,
        severity: 'error',
        message: `Le Workspace "${ws.data?.label || ws.id}" doit être dans une Fabric Capacity ou un Fabric Domain (parent actuel : ${parent.data?.type}).`,
      });
    }
  });

  // 3. fabric_folder must be inside a fabric_workspace
  fabricFolders.forEach(folder => {
    const parent = folder.parentNode ? nodeMap.get(folder.parentNode) : undefined;
    if (!folder.parentNode || !parent) {
      issues.push({
        id: `${folder.id}-folder-no-parent`,
        nodeId: folder.id,
        severity: 'error',
        message: `Le Dossier "${folder.data?.label || folder.id}" n'est pas dans un Workspace. Glissez-le dans un fabric_workspace.`,
      });
    } else if (parent.data?.type !== 'fabric_workspace') {
      issues.push({
        id: `${folder.id}-folder-wrong-parent`,
        nodeId: folder.id,
        severity: 'error',
        message: `Le Dossier "${folder.data?.label || folder.id}" doit être dans un Workspace (parent actuel : ${parent.data?.type}).`,
      });
    }
  });

  // 4. Fabric items must be inside a workspace or folder
  fabricItems.forEach(item => {
    const parent = item.parentNode ? nodeMap.get(item.parentNode) : undefined;
    if (!item.parentNode || !parent) {
      issues.push({
        id: `${item.id}-item-no-parent`,
        nodeId: item.id,
        severity: 'error',
        message: `L'item "${item.data?.label || item.data?.type}" n'est dans aucun Workspace ni Dossier. Glissez-le dans un workspace ou folder.`,
      });
    } else if (parent.data?.type !== 'fabric_workspace' && parent.data?.type !== 'fabric_folder') {
      issues.push({
        id: `${item.id}-item-wrong-parent`,
        nodeId: item.id,
        severity: 'error',
        message: `L'item "${item.data?.label || item.data?.type}" doit être dans un Workspace ou un Dossier (parent actuel : ${parent.data?.type}).`,
      });
    }
  });

  // 5. fabric_capacity SKU must be valid
  fabricCapacities.forEach(cap => {
    const sku: string = cap.data?.params?.sku || '';
    if (!sku) {
      issues.push({
        id: `${cap.id}-cap-no-sku`,
        nodeId: cap.id,
        severity: 'warning',
        message: `La Capacity "${cap.data?.label || cap.id}" n'a pas de SKU défini. Ouvrez ses propriétés pour choisir F2→F128.`,
        field: 'sku',
      });
    } else if (!FABRIC_SKUS.includes(sku)) {
      issues.push({
        id: `${cap.id}-cap-invalid-sku`,
        nodeId: cap.id,
        severity: 'warning',
        message: `SKU "${sku}" non reconnu pour la Capacity "${cap.data?.label || cap.id}". Valeurs attendues : ${FABRIC_SKUS.join(', ')}.`,
        field: 'sku',
      });
    }
  });

  // 6. Workspace with no items → warning
  fabricWorkspaces.forEach(ws => {
    const children = nodes.filter(n =>
      (n.parentNode === ws.id && FABRIC_ITEM_TYPES.has(n.data?.type)) ||
      (n.parentNode && nodeMap.get(n.parentNode)?.parentNode === ws.id && FABRIC_ITEM_TYPES.has(n.data?.type))
    );
    if (children.length === 0) {
      issues.push({
        id: `${ws.id}-ws-empty`,
        nodeId: ws.id,
        severity: 'warning',
        message: `Le Workspace "${ws.data?.label || ws.id}" ne contient aucun item Fabric (lakehouse, pipeline, notebook…).`,
      });
    }
  });

  // 7. F2 capacity with more than 2 workspaces → warning (capacity sizing)
  // Count workspaces directly in capacity OR in a domain that is in the capacity
  fabricCapacities.forEach(cap => {
    const sku: string = cap.data?.params?.sku || 'F2';
    const domainsInCap = new Set(nodes.filter(n => n.data?.type === 'fabric_domain' && n.parentNode === cap.id).map(n => n.id));
    const wsCount = fabricWorkspaces.filter(ws =>
      ws.parentNode === cap.id || domainsInCap.has(ws.parentNode || '')
    ).length;
    if (sku === 'F2' && wsCount > 2) {
      issues.push({
        id: `${cap.id}-cap-undersized`,
        nodeId: cap.id,
        severity: 'warning',
        message: `La Capacity F2 héberge ${wsCount} workspaces. Envisagez de passer à F8 ou F16 pour éviter la saturation des CUs.`,
        field: 'sku',
      });
    }
  });

  // 8. No deployment pipeline → info
  const hasDeploymentPipeline = fabricItems.some(n => n.data?.type === 'fabric_deployment_pipeline');
  if (!hasDeploymentPipeline && fabricWorkspaces.length >= 2) {
    issues.push({
      id: 'fabric-no-deployment-pipeline',
      nodeId: fabricWorkspaces[0]?.id || '',
      severity: 'info',
      message: `Conseil : ajoutez un nœud fabric_deployment_pipeline pour gérer la promotion entre workspaces (dev→staging→prod).`,
    });
  }

  // ── Governance rules ─────────────────────────────────────────────

  // 9. Purview sans nom → error
  const purviewNodes = nodes.filter(n => n.data?.type === 'azurerm_purview_account');
  purviewNodes.forEach(pv => {
    const name: string = pv.data?.params?.name || pv.data?.label || '';
    if (!name || name === 'purview-governance') {
      issues.push({
        id: `${pv.id}-purview-no-name`,
        nodeId: pv.id,
        severity: 'warning',
        message: `Microsoft Purview "${pv.data?.label || pv.id}" : renseignez un nom unique dans les propriétés (ex: purview-${pv.data?.label?.toLowerCase().replace(/\s+/g, '-') || 'prod'}).`,
        field: 'name',
      });
    }
    const rg: string = pv.data?.params?.resource_group_name || '';
    if (!rg) {
      issues.push({
        id: `${pv.id}-purview-no-rg`,
        nodeId: pv.id,
        severity: 'error',
        message: `Microsoft Purview "${pv.data?.label || pv.id}" : le champ resource_group_name est requis pour le déploiement Terraform.`,
        field: 'resource_group_name',
      });
    }
  });

  // 10. fabric_domain sans workspace → info
  const fabricDomains = nodes.filter(n => n.data?.type === 'fabric_domain');
  if (fabricDomains.length > 0 && fabricWorkspaces.length === 0) {
    fabricDomains.forEach(domain => {
      issues.push({
        id: `${domain.id}-domain-no-workspace`,
        nodeId: domain.id,
        severity: 'info',
        message: `Le Fabric Domain "${domain.data?.label || domain.id}" est vide — ajoutez des workspaces à associer à ce domaine métier.`,
      });
    });
  }

  // 11. azurerm_policy_assignment sans scope → warning
  const policyNodes = nodes.filter(n => n.data?.type === 'azurerm_policy_assignment');
  policyNodes.forEach(policy => {
    const scope: string = policy.data?.params?.scope || '';
    if (!scope) {
      issues.push({
        id: `${policy.id}-policy-no-scope`,
        nodeId: policy.id,
        severity: 'warning',
        message: `Compliance Policy "${policy.data?.label || policy.id}" : définissez un scope (subscription ID, resource group…) dans les propriétés.`,
        field: 'scope',
      });
    }
  });

  // 12. Governance sans Purview → info (si architecture Fabric avec plusieurs workspaces)
  if (purviewNodes.length === 0 && fabricWorkspaces.length >= 3) {
    issues.push({
      id: 'fabric-no-purview',
      nodeId: fabricCapacities[0]?.id || fabricWorkspaces[0]?.id || '',
      severity: 'info',
      message: `Conseil : avec ${fabricWorkspaces.length} workspaces, envisagez d'ajouter Microsoft Purview (catégorie Governance) pour la gouvernance et la data lineage.`,
    });
  }

  return issues;
}

export const validateCanvas = (nodes: Node[], edges: Edge[]): ValidationIssue[] => {
  const issues: ValidationIssue[] = [];

  // Validate all nodes
  nodes.forEach(node => {
    issues.push(...validateNode(node));
  });

  // Validate all edges
  edges.forEach(edge => {
    issues.push(...validateEdge(edge, nodes));
  });

  // Fabric-specific structural validation
  issues.push(...validateFabricCanvas(nodes));

  // Check for orphaned nodes (no connections)
  const resourceNodes = nodes.filter(n => n.type === 'custom');
  const connectedNodeIds = new Set([
    ...edges.map(e => e.source),
    ...edges.map(e => e.target),
  ]);

  resourceNodes.forEach(node => {
    if (!connectedNodeIds.has(node.id)) {
      issues.push({
        id: `${node.id}-orphaned`,
        nodeId: node.id,
        severity: 'info',
        message: `Cette ressource n'est connectée à aucune autre ressource`,
      });
    }
  });

  // Check for duplicate names in same resource type
  const namesByType: Record<string, Map<string, string[]>> = {};
  resourceNodes.forEach(node => {
    const type = node.data.type;
    const name = node.data.params?.name;
    if (type && name) {
      if (!namesByType[type]) {
        namesByType[type] = new Map();
      }
      const existing = namesByType[type].get(name) || [];
      existing.push(node.id);
      namesByType[type].set(name, existing);
    }
  });

  Object.entries(namesByType).forEach(([type, nameMap]) => {
    nameMap.forEach((nodeIds, name) => {
      if (nodeIds.length > 1) {
        nodeIds.forEach(nodeId => {
          issues.push({
            id: `${nodeId}-duplicate-name`,
            nodeId,
            severity: 'error',
            message: `Nom "${name}" dupliqué pour le type ${type}`,
            field: 'name',
          });
        });
      }
    });
  });

  return issues;
};
